// Admin → Activities tab: volume by action type, hour×weekday heatmap, security list.
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import EChart from '../charts/EChart';
import { ChartCard, DataTable, EmptyState, ExportButton, exportToCsv, formatDateTime, actionBadgeColor } from './shared';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const AdminActivities = ({ view, t, period }) => {
  const { recentActivities = [] } = view;

  const byTypeOption = useMemo(() => {
    const counts = {};
    recentActivities.forEach((a) => {
      const key = String(a.actionType || 'OTHER').toUpperCase();
      counts[key] = (counts[key] || 0) + 1;
    });
    const keys = Object.keys(counts);
    if (!keys.length) return null;
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 48, right: 16, top: 24, bottom: 48 },
      xAxis: { type: 'category', data: keys, axisLabel: { rotate: 20, fontSize: 10 } },
      yAxis: { type: 'value' },
      series: [{ type: 'bar', data: keys.map((k) => counts[k]), itemStyle: { borderRadius: [4, 4, 0, 0] } }]
    };
  }, [recentActivities]);

  const heatOption = useMemo(() => {
    if (!recentActivities.length) return null;
    const matrix = Array.from({ length: 7 }, () => Array(24).fill(0));
    recentActivities.forEach((a) => {
      if (!a.timestamp) return;
      const d = new Date(a.timestamp);
      if (Number.isNaN(d.getTime())) return;
      const day = (d.getDay() + 6) % 7; // Monday-first
      matrix[day][d.getHours()] += 1;
    });
    const data = [];
    let max = 0;
    matrix.forEach((row, day) => row.forEach((v, hour) => {
      if (v > max) max = v;
      data.push([hour, day, v]);
    }));
    if (max === 0) return null;
    return {
      tooltip: { position: 'top' },
      grid: { left: 56, right: 16, top: 16, bottom: 32 },
      xAxis: { type: 'category', data: Array.from({ length: 24 }, (_, h) => `${h}h`), splitArea: { show: true } },
      yAxis: { type: 'category', data: DAYS, splitArea: { show: true } },
      visualMap: { min: 0, max, calculable: true, orient: 'horizontal', left: 'center', bottom: 0 },
      series: [{ type: 'heatmap', data, label: { show: false }, emphasis: { label: { show: true } } }]
    };
  }, [recentActivities]);

  const securityEvents = useMemo(() => {
    const isSecurity = (a) => {
      const k = String(a.actionType || '').toUpperCase();
      return k.includes('DELETE') || k.includes('FAIL') || k.includes('BLOCK') || k.includes('DENY');
    };
    return recentActivities.filter(isSecurity).slice(0, 20);
  }, [recentActivities]);

  const activityColumns = useMemo(() => ([
    { key: 'actionType', label: t('colAction'), render: (r) => (
      <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${actionBadgeColor(r.actionType)}`}>{r.actionType}</span>
    ) },
    { key: 'userName', label: t('colUser'), render: (r) => r.userName || r.userId || '—' },
    { key: 'timestamp', label: t('colDate'), render: (r) => formatDateTime(r.timestamp) }
  ]), [t]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t('volumeTitle')} subtitle={t('volumeSubtitle', { days: period })}>
          {byTypeOption ? <EChart option={byTypeOption} height={300} /> : <EmptyState message={t('noActivity')} />}
        </ChartCard>
        <ChartCard title={t('heatmapTitle')} subtitle={t('heatmapSubtitle')}>
          {heatOption ? <EChart option={heatOption} height={300} /> : <EmptyState message={t('noActivity')} />}
        </ChartCard>
      </div>

      <ChartCard
        title={t('securityTitle')}
        subtitle={t('securitySubtitle')}
        action={securityEvents.length > 0 ? (
          <ExportButton label={t('exportCsv')} onClick={() => exportToCsv('security-events.csv', [
            { key: 'actionType', label: t('colAction') },
            { key: 'userName', label: t('colUser') },
            { key: 'timestamp', label: t('colDate') }
          ], securityEvents)} />
        ) : null}
      >
        <DataTable columns={activityColumns} rows={securityEvents} emptyMessage={t('noSecurity')} />
        <div className="mt-4 text-right">
          <Link to="/admin/activities" className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800">
            {t('viewLogs')}
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </ChartCard>
    </div>
  );
};

AdminActivities.propTypes = {
  view: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  period: PropTypes.number.isRequired
};

export default AdminActivities;
